import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { GoalStatus, OrderDirection } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';

import { FakeCompanyId } from 'webapp/components/goal/inline-group';

import type { FilterIdType } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Filter from 'webapp/models-custom/filter-sort/filter';
import type Goal from 'webapp/models-custom/goal';

export const settingsNamePrefix = 'goalsFilterSettings'; // used to save filter preferences

const timeframeFilterId = 9;

export default abstract class FilterSortGoal extends FilterSort<Goal> {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/groups') declare mmGroupsService: Service.ModelManager.Groups;
	@service('popup') declare popupService: Service.Popup;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override searchProps: SearchProps = {
		title: 1,
		mainMetricSetupKey: 1,
		'mainMetric.name': 1,
		'sourceCM.title': 1,
		'sourceCM.subtitle': 1,
		otherMetricKey: 1,
		'otherMetric.name': 1,
		'otherMetric.source.title': 1,
		'otherMetric.source.subtitle': 1,
		'ownerUser.name': 1,
	};

	abstract viewSettingsNamePrefix: string;
	abstract override settingsNamePrefix: string;

	override trackingObjectName = 'goal';

	override get viewOptions(): FilterOption<string>[] {
		return [
			{ id: 'table', text: 'List view', icon: 'List' },
			{ id: 'grid', text: 'Grid view', icon: 'Grid' },
		];
	}

	get statusOptions(): FilterOption<number>[] {
		const list: { id: number; text: string }[] = [
			{ id: 0, text: 'All' },
			{ id: GoalStatus.ON_TRACK, text: 'On track' },
			{ id: GoalStatus.AT_RISK, text: 'At risk' },
			{ id: GoalStatus.OFF_TRACK, text: 'Off track' },
			{ id: GoalStatus.ACHIEVED, text: 'Achieved' },
			{ id: GoalStatus.PARTIAL, text: 'Partial' },
			{ id: GoalStatus.MISSED, text: 'Missed' },
		];

		return list;
	}

	@cached
	get ownerOptions(): FilterOption<number>[] {
		const allItemCountByOwner: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const ownerId = item.ownerId || 0;
			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}
			allItemCountByOwner[ownerId]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByOwner);
	}

	@cached
	get groupOptions(): FilterOption<string>[] {
		const groupOptions = this.mmGroupsService.groupModels.map((group) => {
			return {
				id: group.id!,
				text: group.name,
			};
		});

		const sortedGroupOptions = sortBy(groupOptions, 'text');
		sortedGroupOptions.unshift(
			{ id: 'all', text: 'All' },
			{ id: FakeCompanyId, text: this.accountService.company || 'Company' },
		);

		return sortedGroupOptions;
	}

	get timeframeOptions(): FilterOption<string>[] {
		const timeframeFilter = this.filters.find(
			(filter) => filter.id === timeframeFilterId,
		) as Filter<Goal, string>;
		return this.timeframeService.getTimeframeOptionsForFilter(
			timeframeFilter as Filter<unknown, string>,
		);
	}

	protected createTimeframeFilter(selectedFilters: (FilterIdType[] | undefined)[]) {
		const timeframeFilter: Filter<Goal, string> = this.createFilter<string>({
			id: timeframeFilterId,
			label: 'Timeframe',
			name: 'timeframe',
			selected: selectedFilters[timeframeFilterId] as string | undefined,
			isMultiSelect: false,
			stringValues: true,
			getOptions: () => {
				return this.timeframeOptions;
			},
			filterFn: (item, selected) => {
				const timeframe = this.timeframeService.getTimeframeFromStringForFilter(
					selected[0] || '',
				);
				if (timeframe === undefined) {
					// invalid, so fallback to all
					return true;
				} else {
					return this.timeframeService.isMatchingTimeframe(item, timeframe);
				}
			},
			createPopupFn: (attachTo: HTMLElement, _extraClass: string, theme?: string) => {
				return this.timeframeService.openTimeframePopupForFilter(
					timeframeFilter as Filter<unknown, string>,
					attachTo,
					theme,
				);
			},
		});

		return timeframeFilter;
	}
}
